import * as React from 'react';
import { clsx } from 'clsx';

export type InputSize = 'small' | 'medium';
export type ValidationState = 'default' | 'error' | 'success';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * The size of the input
   * @default 'medium'
   */
  size?: InputSize;
  /**
   * The validation state of the input
   * @default 'default'
   */
  state?: ValidationState;
  /**
   * Optional label for the input
   */
  label?: string;
  /**
   * Optional error message to display
   */
  error?: string;
  /**
   * Optional helper text to display
   */
  helperText?: string;
}

// Arbor Design System input styles
const inputStyles = {
  base: {
    width: '100%',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    outline: 'none',
    margin: '0',
    display: 'block',
  },
  sizes: {
    small: {
      height: '32px',
      fontSize: '13px',
      padding: '8px',
    },
    medium: {
      height: '36px',
      fontSize: '13px',
      padding: '8px',
    },
  },
  states: {
    default: {
      border: '1px solid #d1d1d1', // grey-300
      color: '#2f2f2f', // grey-900
      ':focus': {
        borderColor: '#3cad51', // brand-500
        outline: '3px solid rgba(60, 173, 81, 0.2)',
      },
      ':disabled': {
        backgroundColor: '#f8f8f8', // grey-050
        borderColor: '#efefef', // grey-100
        color: '#7e7e7e', // grey-500
        cursor: 'not-allowed',
      },
    },
    error: {
      border: '1px solid #c93232', // destructive-500
      color: '#2f2f2f',
      ':focus': {
        borderColor: '#c93232',
        outline: '3px solid rgba(201, 50, 50, 0.2)',
      },
    },
    success: {
      border: '1px solid #16a33d', // success-500
      color: '#2f2f2f',
      ':focus': {
        borderColor: '#16a33d',
        outline: '3px solid rgba(22, 163, 61, 0.2)',
      },
    },
  },
};

const labelStyles: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#2f2f2f', // grey-900
  marginBottom: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '13px',
  margin: '0',
  marginTop: '2px',
  color: '#595959', // grey-600
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lineHeight: '1.4',
};

const errorTextStyles: React.CSSProperties = {
  ...helperTextStyles,
  color: '#a62323', // destructive-600 (accessible)
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

/**
 * Input component - Arbor Design System
 *
 * A text input component following Arbor's design specifications.
 * Supports labels, validation states (error/success), and helper text.
 * Uses 8px border radius and Inter font family.
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="you@example.com"
 *   helperText="We'll never share your email"
 * />
 * ```
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'medium',
      state = 'default',
      label,
      error,
      helperText,
      className,
      style,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId();
    const helperTextId = React.useId();
    const errorId = React.useId();
    const [isFocused, setIsFocused] = React.useState(false);

    const sizeStyles = inputStyles.sizes[size];
    const stateStyles = inputStyles.states[error ? 'error' : state];

    const inputStyle: React.CSSProperties = {
      ...inputStyles.base,
      ...sizeStyles,
      ...stateStyles,
      ...(isFocused && !disabled && stateStyles[':focus']),
      ...(disabled && inputStyles.states.default[':disabled']),
    };

    return (
      <div className={clsx('arbor-input-wrapper', className)} style={style}>
        {label && (
          <label htmlFor={inputId} style={labelStyles}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className="arbor-input"
          style={inputStyle}
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? errorId : helperText ? helperTextId : undefined
          }
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />
        {error && (
          <p id={errorId} style={errorTextStyles} role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={helperTextId} style={helperTextStyles}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
